import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import TeamSearchBar from './TeamSearchBar';
import TeamFilterChips from './TeamFilterChips';
import TeamTable from './TeamTable';
import TeamPagination from './TeamPagination';
import {
  filterAndSortMembers,
  computeMembersByRole,
  computeMembersByVenue,
  computeMembersByTeamFunction
} from '../utils/teamFilterUtils';

const TeamMemberCard = ({
  // Data
  teamMemberships,
  pendingInvitations,
  roles,
  venueOwnerships,
  team,
  csrfToken,
  // Search & Filter state
  searchTerm,
  roleFilter,
  venueFilter,
  teamFunctions,
  teamFunctionFilter,
  currentPage,
  pageSize,
  sortBy,
  sortDirection,
  selectedMemberIds,
  // Search & Filter actions
  onSearchChange,
  onRoleFilterChange,
  onVenueFilterChange,
  onTeamFunctionFilterChange,
  onClearFilters,
  onPageChange,
  onSortChange,
  // Selection actions
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  // CRUD actions
  onAddMember,
  onEdit,
  onDelete,
  onResetPassword
}) => {
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Combine all members (sorting is applied after filters based on current sort state)
  const allMembers = useMemo(() => {
    return [...teamMemberships, ...pendingInvitations];
  }, [teamMemberships, pendingInvitations]);

  // Filter and sort members based on search, role filter, venue filter, and sort state
  const filteredMembers = useMemo(() => (
    filterAndSortMembers({
      allMembers,
      roles,
      searchTerm,
      roleFilter,
      venueFilter,
      teamFunctionFilter,
      sortBy,
      sortDirection
    })
  ), [allMembers, roles, searchTerm, roleFilter, venueFilter, teamFunctionFilter, sortBy, sortDirection]);

  // Paginate filtered members
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredMembers.slice(startIndex, startIndex + pageSize);
  }, [filteredMembers, currentPage, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredMembers.length / pageSize);

  // Determine which members are currently selected on the *current page only*
  const selectedMembers = useMemo(() => {
    return paginatedMembers.filter(member =>
      selectedMemberIds.includes(`${member.type_for_ui}-${member.id}`)
    );
  }, [paginatedMembers, selectedMemberIds]);

  // Calculate members by role for filter chips
  const membersByRole = useMemo(() => (
    computeMembersByRole(allMembers)
  ), [allMembers]);

  // Calculate members by venue for filter chips
  const membersByVenue = useMemo(() => (
    computeMembersByVenue(allMembers, venueOwnerships)
  ), [allMembers, venueOwnerships]);

  const membersByTeamFunction = useMemo(() => (
    computeMembersByTeamFunction(allMembers)
  ), [allMembers]);

  return (
    <div className="team-member-card">
      {/* Header */}
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Team members</h2>
            <span className="team-member-count-badge">{allMembers.length} users</span>
          </div>
          <p className="team-member-card-subtitle">
            Manage your team members and their account permissions here.
          </p>
        </div>
        <button className="team-btn-primary" onClick={onAddMember}>
          <i className="fal fa-plus" style={{ fontSize: '14px' }}></i>
          <span>Add team member</span>
        </button>
      </div>

      {/* Search and Filters Row */}
      <div className="team-search-filters-row">
        <div className="team-search-filters-left">
          <TeamSearchBar
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            placeholder="Search team"
          />
        </div>
        <div className="team-search-filters-right">
          <TeamFilterChips
            roles={roles}
            roleFilter={roleFilter}
            venueOwnerships={venueOwnerships}
            venueFilter={venueFilter}
            teamFunctions={teamFunctions}
            teamFunctionFilter={teamFunctionFilter}
            onRoleFilterChange={onRoleFilterChange}
            onVenueFilterChange={onVenueFilterChange}
            onTeamFunctionFilterChange={onTeamFunctionFilterChange}
            onClearFilters={onClearFilters}
            membersByRole={membersByRole}
            membersByVenue={membersByVenue}
            membersByTeamFunction={membersByTeamFunction}
            showDeleteSelected={selectedMembers.length > 0}
            onDeleteSelectedClick={() => setBulkDeleteOpen(true)}
          />
        </div>
      </div>

      {/* Table */}
      <TeamTable
        members={paginatedMembers}
        selectedMemberIds={selectedMemberIds}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onToggleSelect={onToggleSelect}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        onEdit={onEdit}
        onDelete={onDelete}
        onResetPassword={onResetPassword}
        csrfToken={csrfToken}
        team={team}
        onSortChange={onSortChange}
      />

      {/* Pagination */}
      <TeamPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredMembers.length}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />

      {bulkDeleteOpen && (
        <div className="team-modal-overlay">
          <div className="team-modal">
            <div className="team-modal-header">
              <h3 className="team-modal-title">Delete selected team members?</h3>
              <button
                className="team-modal-close"
                onClick={() => setBulkDeleteOpen(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="team-modal-body">
              <p style={{ marginBottom: '8px' }}>
                You're about to delete <strong>{selectedMembers.length}</strong> team member{selectedMembers.length === 1 ? '' : 's'}.
              </p>
              <p style={{ marginBottom: '8px' }}>This cannot be undone.</p>
              <p>Are you sure?</p>
            </div>
            <div className="team-modal-footer">
              <button
                className="team-btn-secondary"
                onClick={() => setBulkDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                className="team-btn-danger"
                onClick={(e) => {
                  e.preventDefault();
                  selectedMembers.forEach(member => {
                    onDelete(csrfToken, team, member, () => {});
                  });
                  onDeselectAll();
                  setBulkDeleteOpen(false);
                }}
              >
                Yes, delete selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TeamMemberCard.propTypes = {
  teamMemberships: PropTypes.array.isRequired,
  pendingInvitations: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  venueOwnerships: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  roleFilter: PropTypes.array,
  venueFilter: PropTypes.array,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  selectedMemberIds: PropTypes.array.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onRoleFilterChange: PropTypes.func.isRequired,
  onVenueFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onAddMember: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired
};

export default TeamMemberCard;
